// Your notes (module 10 U209; pipeline I2, REQ-2026-10-04-31), at /deals/notes: every note you have added, newest
// first, each with what became of it. A read note shows its words beside the page they were read from, with the words
// the reader was not sure of marked for you to check. Only your own: nobody else's notes are listed, or could be.
import { ActionButton } from '../parts/ActionButton.js';
import { useState } from 'react';
import { Failed, Loading, useLoad, type PageProps } from '../records/shared.js';
import { AddNotes } from './AddNotes.js';
import { notesApi, type Note } from './api.js';
import { markedParts, noteStateWords, unmarked, whenWords } from './words.js';
import './notes.css';

const PICTURE = new Set(['photo']);

export function NotesPage({ store, open }: PageProps) {
  const [loaded, reload] = useLoad(() => notesApi.list(store.tenantId), [store.tenantId]);
  return (
    <div className="deals nt">
      <div className="dl-top">
        <h1 className="dl-title">Your notes</h1>
        <ActionButton reading type="button" className="nt-back" onClick={() => open('/deals')}>Deals</ActionButton>
      </div>
      <AddNotes store={store.tenantId} onAdded={reload} />
      {loaded.kind === 'loading' ? <Loading what="your notes" />
        : loaded.kind === 'error' ? <Failed message={loaded.message} />
        : loaded.data.notes.length === 0 ? <p className="dl-says gist">No notes of yours yet.</p>
        : <div className="nt-list">{loaded.data.notes.map((n) => <NoteView key={n.id} n={n} store={store.tenantId} />)}</div>}
      {/* BUGS.md D113: the list is the newest 50; say so when there are more, as a company's notes do (CompanyNotes). */}
      {loaded.kind === 'ready' && loaded.data.total > loaded.data.notes.length
        ? <p className="dl-says gist">The newest {loaded.data.notes.length} of {loaded.data.total.toLocaleString('en-GB')} are listed.</p> : null}
    </div>
  );
}

function NoteView({ n, store }: { n: Note; store: string }) {
  const [wide, setWide] = useState(false);
  const state = noteStateWords(n);
  const words = n.text ?? '';
  const extra = unmarked(words, n.unsure);
  return (
    <article className="nt-note" aria-label={n.name}>
      <header className="nt-head">
        <b>{n.subject?.replace(/^Note:\s*/, '') || n.name}</b>
        <small>{[n.meeting, whenWords(n.uploadedAt), n.subject ? n.name : null].filter(Boolean).join(' · ')}</small>
        <span className={state.tone === 'warn' ? 'nt-warn' : 'nt-meta'}>{state.text}</span>
      </header>
      {n.state === 'read' && words
        ? (
          <div className={`nt-body${n.hasPage && PICTURE.has(n.kind) ? ' with-page' : ''}`}>
            <div className="nt-words">
              {n.unsure.length > 0 ? <p className="nt-check">Check the marked words against the page.</p> : null}
              <p className="nt-text">{markedParts(words, n.unsure).map((p, i) => (p.unsure ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}</p>
              {extra.length > 0 ? <p className="nt-check">Also unsure: {extra.map((u) => u.text).join(', ')}</p> : null}
            </div>
            {n.hasPage && PICTURE.has(n.kind)
              ? (
                <ActionButton reading type="button" className={`nt-page${wide ? ' wide' : ''}`} onClick={() => setWide(!wide)} aria-label={wide ? 'Show the page smaller' : 'Show the page larger'}>
                  <img src={notesApi.pageUrl(store, n.id)} alt={`The page: ${n.name}`} loading="lazy" />
                </ActionButton>
              )
              : null}
          </div>
        )
        : null}
      {n.hasPage && !PICTURE.has(n.kind) ? <a className="nt-file" href={notesApi.pageUrl(store, n.id)} target="_blank" rel="noopener">Open the file</a> : null}
    </article>
  );
}
